/**
 * The 5-field UTC cron of nyce.buddy monitors (`minute hour day-of-month month day-of-week`): validation and an English
 * preview. Same grammar as buddy3's `CronSchedule`: `*`, `*\/n`, `a-b`, `a,b`, `a-b/n`, `a/n`, month and weekday
 * names, weekday 0 and 7 = Sunday.
 */

const FIELDS = [
  { name: 'minute', min: 0, max: 59 },
  { name: 'hour', min: 0, max: 23 },
  { name: 'day of month', min: 1, max: 31 },
  { name: 'month', min: 1, max: 12 },
  { name: 'day of week', min: 0, max: 7 },
] as const;

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

interface Part {
  /** `null` = `*`. */
  from: number | null;
  to: number | null;
  step: number;
}

interface Field {
  parts: Part[];
  /** Every value of the range (`*` or the full range with step 1). */
  any: boolean;
}

function parseValue(v: string, index: number): number | null {
  const names = index === 3 ? MONTHS : index === 4 ? DAYS : null;
  const i = names ? names.indexOf(v.toLowerCase()) : -1;
  if (i >= 0) return index === 3 ? i + 1 : i;
  return /^\d+$/.test(v) ? Number(v) : null;
}

function parseField(raw: string, index: number): Field | string {
  const { name, min, max } = FIELDS[index];
  const parts: Part[] = [];
  for (const piece of raw.split(',')) {
    if (!piece) return `${name}: empty list item`;
    const [rangeRaw, stepRaw, extra] = piece.split('/');
    if (extra !== undefined) return `${name}: '${piece}' has more than one '/'`;
    let step = 1;
    if (stepRaw !== undefined) {
      if (!/^\d+$/.test(stepRaw) || Number(stepRaw) < 1)
        return `${name}: step '${stepRaw}' must be a positive number`;
      step = Number(stepRaw);
    }
    if (rangeRaw === '*') {
      parts.push({ from: null, to: null, step });
      continue;
    }
    const [aRaw, bRaw, more] = rangeRaw.split('-');
    if (more !== undefined) return `${name}: '${rangeRaw}' is not a range`;
    const a = parseValue(aRaw, index);
    if (a === null) return `${name}: '${aRaw}' is not a value`;
    if (a < min || a > max) return `${name}: ${a} is outside ${min}-${max}`;
    if (bRaw === undefined) {
      // `a/n` = from a to the end of the range
      parts.push({ from: a, to: stepRaw !== undefined ? max : a, step });
      continue;
    }
    const b = parseValue(bRaw, index);
    if (b === null) return `${name}: '${bRaw}' is not a value`;
    if (b < min || b > max) return `${name}: ${b} is outside ${min}-${max}`;
    if (b < a) return `${name}: range ${a}-${b} is reversed`;
    parts.push({ from: a, to: b, step });
  }
  const p = parts[0];
  const any = parts.length === 1 && p.step === 1 && (p.from === null || (p.from === min && p.to === max));
  return { parts, any };
}

function parse(expr: string): Field[] | string {
  const fields = expr.trim().split(/\s+/);
  if (fields.length !== 5) return `expected 5 fields (minute hour day month weekday), got ${fields.length}`;
  const out: Field[] = [];
  for (let i = 0; i < 5; i++) {
    const f = parseField(fields[i], i);
    if (typeof f === 'string') return f;
    out.push(f);
  }
  return out;
}

/** `null` when the expression is valid, otherwise the reason. */
export function validateCron(expr: string | null | undefined): string | null {
  if (!expr?.trim()) return 'cron expression is required';
  const r = parse(expr);
  return typeof r === 'string' ? r : null;
}

const two = (n: number) => String(n).padStart(2, '0');

/** The one literal value of a field, else null. */
const single = (f: Field) =>
  f.parts.length === 1 && f.parts[0].from !== null && f.parts[0].from === f.parts[0].to
    ? f.parts[0].from
    : null;

/** `*\/n` with n > 1, else null. */
const everyN = (f: Field) =>
  f.parts.length === 1 && f.parts[0].from === null && f.parts[0].step > 1 ? f.parts[0].step : null;

/** A plain `a-b` range (step 1), else null. */
const range = (f: Field) =>
  f.parts.length === 1 && f.parts[0].from !== null && f.parts[0].step === 1
    ? { from: f.parts[0].from, to: f.parts[0].to ?? f.parts[0].from }
    : null;

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

function values(f: Field, min: number, max: number): number[] {
  const set = new Set<number>();
  for (const p of f.parts) for (let v = p.from ?? min; v <= (p.to ?? max); v += p.step) set.add(v);
  return [...set].sort((a, b) => a - b);
}

function weekdayText(f: Field): string {
  const days = [...new Set(values(f, 0, 7).map((d) => d % 7))].sort((a, b) => a - b);
  const contiguous = days.every((d, i) => i === 0 || d === days[i - 1] + 1);
  if (days.length > 2 && contiguous) return `${DAY_NAMES[days[0]]}–${DAY_NAMES[days[days.length - 1]]}`;
  return days.map((d) => DAY_NAMES[d]).join(', ');
}

const monthText = (f: Field) =>
  values(f, 1, 12)
    .map((m) => MONTH_NAMES[m - 1])
    .join(', ');

/** English description of a 5-field UTC cron; "Invalid cron expression" when it does not parse. */
export function describeCron(expr: string | null | undefined): string {
  const r = parse(expr ?? '');
  if (typeof r === 'string') return 'Invalid cron expression';
  const [minute, hour, dom, month, dow] = r;
  const custom = `Custom: ${(expr ?? '').trim()}`;
  const m = single(minute);
  const h = single(hour);
  const at = m !== null && h !== null ? `${two(h)}:${two(m)} UTC` : null;

  // Sub-daily cadences: day, month and weekday unrestricted.
  if (dom.any && month.any && dow.any) {
    const nMin = everyN(minute);
    if (hour.any) {
      if (minute.any) return 'Every minute';
      if (nMin) return `Every ${nMin} minutes`;
      if (m !== null) return m === 0 ? 'Every hour' : `Every hour at :${two(m)}`;
      return custom;
    }
    const nHour = everyN(hour);
    if (nHour && m !== null) return `Every ${nHour} hours at :${two(m)}`;
    if (at) return `Every day at ${at}`;
    const hours = range(hour);
    if (hours && m !== null)
      return `Every hour from ${two(hours.from)}:${two(m)} to ${two(hours.to)}:${two(m)} UTC`;
    if (hours && nMin)
      return `Every ${nMin} minutes between ${two(hours.from)}:00 and ${two(hours.to)}:59 UTC`;
    return custom;
  }
  if (!at) return custom;
  if (dom.any && month.any) return `${weekdayText(dow)} at ${at}`;
  if (!dow.any) return custom; // day of month OR weekday: too subtle for a one-liner
  const d = single(dom);
  const days = d !== null ? `the ${ordinal(d)}` : `days ${values(dom, 1, 31).join(', ')}`;
  if (month.any) return `On ${days} of every month at ${at}`;
  if (dom.any) return `Every day in ${monthText(month)} at ${at}`;
  return `On ${days} of ${monthText(month)} at ${at}`;
}
